import React from "react";
import PageHero from "../components/shared/PageHero";
import SectionHeading from "../components/shared/SectionHeading";
import SectionDivider from "../components/decor/SectionDivider";
import CTASection from "../components/shared/CTASection";
import Mandala from "../components/decor/Mandala";
import { IMG, CORE_VALUES, HIGHLIGHTS } from "../data/site";
import { Landmark, Flower2, ScrollText, HandHeart, Music4, Sparkles } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

const iconMap = { Landmark, Flower2, ScrollText, HandHeart, Music4, Sparkles };

const timeline = [
  { period: { en: "13th c.", kn: "೧೩ನೇ ಶತಮಾನ" }, title: { en: "Foundation in the Dvaita tradition", kn: "ದ್ವೈತ ಪರಂಪರೆಯ ಸ್ಥಾಪನೆ" }, text: { en: "Sri Sode Vadiraja Matha is established as one of the eight Ashta Mathas of Udupi by Jagadguru Sri Madhvacharya, entrusted with the worship of Sri Krishna and the propagation of Dvaita philosophy.", kn: "ಜಗದ್ಗುರು ಶ್ರೀ ಮಧ್ವಾಚಾರ್ಯರಿಂದ ಉಡುಪಿಯ ಎಂಟು ಅಷ್ಟಮಠಗಳಲ್ಲಿ ಒಂದಾಗಿ ಶ್ರೀ ಸೋದೆ ವಾದಿರಾಜ ಮಠವು ಸ್ಥಾಪಿಸಲ್ಪಟ್ಟಿತು. ಶ್ರೀ ಕೃಷ್ಣನ ಪೂಜೆ ಮತ್ತು ದ್ವೈತ ಸಿದ್ಧಾಂತದ ಪ್ರಚಾರವನ್ನು ಇದಕ್ಕೆ ವಹಿಸಲಾಯಿತು." } },
  { period: { en: "13th-15th c.", kn: "೧೩-೧೫ನೇ ಶತಮಾನ" }, title: { en: "The Sarvamoola tradition takes root", kn: "ಸರ್ವಮೂಲ ಪರಂಪರೆಯ ಬೇರೂರಿಸುವಿಕೆ" }, text: { en: "The early Peethadhipatis nurture the study of the Sarvamoola Granthas of Sri Madhva and cultivate the community of Vaishnavas across the Konkan and Karnataka coast.", kn: "ಆದಿ ಪೀಠಾಧಿಪತಿಗಳು ಶ್ರೀ ಮಧ್ವರ ಸರ್ವಮೂಲ ಗ್ರಂಥಗಳ ಅಧ್ಯಯನವನ್ನು ಪೋಷಿಸಿ, ಕೊಂಕಣ ಹಾಗೂ ಕರ್ನಾಟಕ ಕರಾವಳಿಯ ವೈಷ್ಣವ ಸಮುದಾಯವನ್ನು ಬೆಳೆಸಿದರು." } },
  { period: { en: "16th c.", kn: "೧೬ನೇ ಶತಮಾನ" }, title: { en: "Sri Vadiraja Theertha - the transformation", kn: "ಶ್ರೀ ವಾದಿರಾಜ ತೀರ್ಥರು - ಪರಿವರ್ತನೆ" }, text: { en: "Sri Vadiraja Theertha ascends the Peetha, composing 'Yuktimallika', 'Rukminisha Vijaya', 'Teerthaprabandha' and hundreds of devotional works while pioneering the Paryaya system of Krishna worship at Udupi.", kn: "ಶ್ರೀ ವಾದಿರಾಜ ತೀರ್ಥರು ಪೀಠಾರೋಹಣ ಮಾಡಿ, 'ಯುಕ್ತಿಮಲ್ಲಿಕಾ', 'ರುಕ್ಮಿಣೀಶ ವಿಜಯ', 'ತೀರ್ಥಪ್ರಬಂಧ' ಮತ್ತು ನೂರಾರು ಭಕ್ತಿ ರಚನೆಗಳನ್ನು ಮಾಡುತ್ತಾ ಉಡುಪಿಯ ಕೃಷ್ಣ ಪೂಜೆಯ ಪರ್ಯಾಯ ವ್ಯವಸ್ಥೆಯನ್ನು ಪ್ರಾರಂಭಿಸಿದರು." } },
  { period: { en: "17th-19th c.", kn: "೧೭-೧೯ನೇ ಶತಮಾನ" }, title: { en: "Expansion of branches & patronage", kn: "ಶಾಖೆಗಳು ಮತ್ತು ಆಶ್ರಯದ ವಿಸ್ತರಣೆ" }, text: { en: "Branch Mathas are established across South and Western India. Royal patronage from Vijayanagara and later dynasties supports temple worship and Vedic scholarship.", kn: "ದಕ್ಷಿಣ ಮತ್ತು ಪಶ್ಚಿಮ ಭಾರತದಾದ್ಯಂತ ಶಾಖಾ ಮಠಗಳು ಸ್ಥಾಪನೆಗೊಂಡವು. ವಿಜಯನಗರ ಹಾಗೂ ನಂತರದ ಸಾಮ್ರಾಜ್ಯಗಳ ರಾಜಾಶ್ರಯವು ದೇವಸ್ಥಾನ ಪೂಜೆ ಮತ್ತು ವೈದಿಕ ವಿದ್ವತ್ತೆಗೆ ಬೆಂಬಲ ನೀಡಿತು." } },
  { period: { en: "20th c.", kn: "೨೦ನೇ ಶತಮಾನ" }, title: { en: "Modern scholarship & publications", kn: "ಆಧುನಿಕ ವಿದ್ವತ್ ಮತ್ತು ಪ್ರಕಟಣೆಗಳು" }, text: { en: "The Matha begins systematic publishing of critical editions, founds Tatvamallika magazine, and expands educational institutions.", kn: "ಮಠವು ವಿಮರ್ಶಾತ್ಮಕ ಆವೃತ್ತಿಗಳ ವ್ಯವಸ್ಥಿತ ಪ್ರಕಟಣೆ ಆರಂಭಿಸಿ, ತತ್ವಮಲ್ಲಿಕಾ ಪತ್ರಿಕೆಯನ್ನು ಸ್ಥಾಪಿಸಿ ಶೈಕ್ಷಣಿಕ ಸಂಸ್ಥೆಗಳನ್ನು ವಿಸ್ತರಿಸಿತು." } },
  { period: { en: "Today", kn: "ಇಂದು" }, title: { en: "Living tradition, global community", kn: "ಜೀವಂತ ಪರಂಪರೆ, ಜಾಗತಿಕ ಸಮುದಾಯ" }, text: { en: "Under the current Peethadhipati, the Matha serves devotees worldwide with daily worship, discourses, publications, festivals, Annadana, and cultural programmes.", kn: "ಪ್ರಸ್ತುತ ಪೀಠಾಧಿಪತಿಗಳ ಮಾರ್ಗದರ್ಶನದಲ್ಲಿ ಮಠವು ಪ್ರಪಂಚದಾದ್ಯಂತದ ಭಕ್ತರಿಗೆ ದೈನಂದಿನ ಪೂಜೆ, ಪ್ರವಚನ, ಪ್ರಕಟಣೆಗಳು, ಉತ್ಸವಗಳು, ಅನ್ನದಾನ ಹಾಗೂ ಸಾಂಸ್ಕೃತಿಕ ಕಾರ್ಯಕ್ರಮಗಳ ಮೂಲಕ ಸೇವೆ ಸಲ್ಲಿಸುತ್ತಿದೆ." } },
];

const ABOUT_COPY = {
  introEye: { en: "Namaste", kn: "ನಮಸ್ತೆ" },
  introT: { en: "Rooted in seven centuries of tradition", kn: "ಏಳು ಶತಮಾನಗಳ ಪರಂಪರೆಯಲ್ಲಿ ಬೇರೂರಿದೆ" },
  introS: { en: "From daily worship of the presiding deities to the propagation of Dvaita philosophy, from Annadana to scholarly research, from classical arts to educational institutions - the Matha touches every dimension of spiritual and cultural life.", kn: "ಪ್ರಧಾನ ದೇವರುಗಳ ದೈನಂದಿನ ಪೂಜೆಯಿಂದ ದ್ವೈತ ಸಿದ್ಧಾಂತದ ಪ್ರಚಾರದವರೆಗೆ, ಅನ್ನದಾನದಿಂದ ಶಾಸ್ತ್ರೀಯ ಸಂಶೋಧನೆಯವರೆಗೆ, ಶಾಸ್ತ್ರೀಯ ಕಲೆಗಳಿಂದ ಶೈಕ್ಷಣಿಕ ಸಂಸ್ಥೆಗಳವರೆಗೆ - ಮಠವು ಆಧ್ಯಾತ್ಮಿಕ ಹಾಗೂ ಸಾಂಸ್ಕೃತಿಕ ಜೀವನದ ಪ್ರತಿ ಆಯಾಮವನ್ನೂ ಸ್ಪರ್ಶಿಸುತ್ತದೆ." },
  introP1: { en: "The Matha is entrusted with the sacred duty of worshipping Sri Bhuvaraha, Sri Hayagriva, Sri Lakshmi Narasimha and other divine forms received in an unbroken chain of tradition. Its Peethadhipati, in the lineage of Sri Vadiraja Theertha, guides devotees in matters of dharma, worship and philosophy.", kn: "ಶ್ರೀ ಭೂವರಾಹ, ಶ್ರೀ ಹಯಗ್ರೀವ, ಶ್ರೀ ಲಕ್ಷ್ಮೀ ನೃಸಿಂಹ ಹಾಗೂ ಅಚ್ಛೇದಿತ ಪರಂಪರೆಯಲ್ಲಿ ಪ್ರಾಪ್ತವಾದ ಇತರ ದೇವಸ್ವರೂಪಗಳನ್ನು ಪೂಜಿಸುವ ಪವಿತ್ರ ಕರ್ತವ್ಯವನ್ನು ಮಠಕ್ಕೆ ವಹಿಸಲಾಗಿದೆ. ಶ್ರೀ ವಾದಿರಾಜ ತೀರ್ಥರ ಪರಂಪರೆಯ ಪೀಠಾಧಿಪತಿಗಳು ಧರ್ಮ, ಪೂಜೆ ಹಾಗೂ ದರ್ಶನ ವಿಷಯಗಳಲ್ಲಿ ಭಕ್ತರಿಗೆ ಮಾರ್ಗದರ್ಶನ ನೀಡುತ್ತಾರೆ." },
  introP2: { en: "Through publications like Tatvamallika, critical editions of classical granthas, seminars and traditional Sanskrit Patashalas, the Matha continues to be a beacon of Dvaita scholarship - while also serving society through free meals, medical support, and support for artists and scholars.", kn: "ತತ್ವಮಲ್ಲಿಕಾದಂತಹ ಪ್ರಕಟಣೆಗಳು, ಶಾಸ್ತ್ರೀಯ ಗ್ರಂಥಗಳ ವಿಮರ್ಶಾತ್ಮಕ ಆವೃತ್ತಿಗಳು, ವಿಚಾರ ಸಂಕಿರಣಗಳು ಮತ್ತು ಸಾಂಪ್ರದಾಯಿಕ ಸಂಸ್ಕೃತ ಪಾಠಶಾಲೆಗಳ ಮೂಲಕ ಮಠವು ದ್ವೈತ ವಿದ್ವತ್ತೆಯ ದೀಪಸ್ತಂಭವಾಗಿ ಮುಂದುವರೆದಿದೆ - ಜೊತೆಗೆ ಉಚಿತ ಊಟ, ವೈದ್ಯಕೀಯ ಸಹಾಯ ಮತ್ತು ಕಲಾವಿದರು-ವಿದ್ವಾಂಸರಿಗೆ ಬೆಂಬಲ ನೀಡುತ್ತಾ ಸಮಾಜ ಸೇವೆ ಸಲ್ಲಿಸುತ್ತಿದೆ." },
  timelineEye: { en: "The Journey", kn: "ಪ್ರವಾಸ" },
  timelineT: { en: "A timeline of the Matha", kn: "ಮಠದ ಇತಿಹಾಸ ಕ್ರಮ" },
  timelineS: { en: "From foundation to the present day - key milestones in the story of Sri Sode Vadiraja Matha.", kn: "ಸ್ಥಾಪನೆಯಿಂದ ಇಂದಿನವರೆಗೆ - ಶ್ರೀ ಸೋದೆ ವಾದಿರಾಜ ಮಠದ ಪ್ರಮುಖ ಮೈಲಿಗಲ್ಲುಗಳು." },
  valuesEye: { en: "Six sacred pillars", kn: "ಆರು ಪವಿತ್ರ ಆಧಾರಸ್ತಂಭಗಳು" },
  valuesT: { en: "Values that guide every moment", kn: "ಪ್ರತಿ ಕ್ಷಣವನ್ನೂ ಮಾರ್ಗದರ್ಶಿಸುವ ಮೌಲ್ಯಗಳು" },
  ctaEye: { en: "Continue the journey", kn: "ಪ್ರವಾಸವನ್ನು ಮುಂದುವರೆಸಿ" },
  ctaT: { en: "Walk deeper into the story", kn: "ಕಥೆಯ ಆಳಕ್ಕೆ ಇಳಿಯಿರಿ" },
  ctaS: { en: "Explore the life and teachings of Sri Vadiraja Theertha and the unbroken Guru Parampara of Sode Matha.", kn: "ಶ್ರೀ ವಾದಿರಾಜ ತೀರ್ಥರ ಜೀವನ, ಬೋಧನೆಗಳು ಹಾಗೂ ಸೋದೆ ಮಠದ ಅಚ್ಛೇದಿತ ಗುರುಪರಂಪರೆಯನ್ನು ಅನ್ವೇಷಿಸಿ." },
  ctaP: { en: "Sri Vadiraja Theertha", kn: "ಶ್ರೀ ವಾದಿರಾಜ ತೀರ್ಥ" },
  ctaSbtn: { en: "Guru Parampara", kn: "ಗುರುಪರಂಪರೆ" },
};

const AboutBody = () => {
  const { t, lang } = useLang();
  const L = (o) => (o && o[lang]) || (o && o.en) || "";
  const pick = (o, k) => (lang === "kn" && o[`${k}Kn`]) || o[k];
  return (
    <>
      <PageHero
        eyebrow={t("about.hero.eyebrow")}
        title={t("about.hero.title")}
        subtitle={t("about.hero.subtitle")}
        image={IMG.templeStone}
      />

      {/* Intro two-column */}
      <section className="py-16" data-testid="about-intro">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div className="relative">
            <div className="aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl">
              <img src={IMG.templeCorridor} alt="Matha corridor" className="w-full h-full object-cover" data-testid="about-image-1" />
            </div>
            <div className="absolute -top-8 -left-8 opacity-70"><Mandala size={160} color="#D4AF37" opacity={0.6} /></div>
          </div>
          <div>
            <SectionHeading
              eyebrow={L(ABOUT_COPY.introEye)}
              title={L(ABOUT_COPY.introT)}
              subtitle={L(ABOUT_COPY.introS)}
            />
            <div className="mt-8 space-y-3 text-[#3B2A1A]/85 leading-relaxed">
              <p>{L(ABOUT_COPY.introP1)}</p>
              <p>{L(ABOUT_COPY.introP2)}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Additional imagery */}
      <section className="pb-24" data-testid="about-gallery">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 grid md:grid-cols-2 gap-6">
          <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-xl border border-[#D4AF37]/30">
            <img
              src={require("../assets/9ft0ujmt_sode_kshetra_Enhanced.webp")}
              alt="Sri Sode Vadiraja Matha - view of the main sannidhi"
              className="w-full h-full object-cover"
              data-testid="about-image-2"
            />
          </div>
          <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-xl border border-[#D4AF37]/30">
            <img
              src={require("../assets/dmte88d4_about_sode.webp")}
              alt="Sri Sode Vadiraja Matha - temple with dhwaja stambha"
              className="w-full h-full object-cover"
              data-testid="about-image-3"
            />
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="bg-sand-texture py-16 relative overflow-hidden" data-testid="about-timeline">
        <div className="absolute -bottom-24 -right-24 opacity-40 animate-mandala-slow"><Mandala size={360} color="#B65A14" opacity={0.4} /></div>
        <div className="max-w-6xl mx-auto px-4 lg:px-8">
          <SectionHeading
            eyebrow={L(ABOUT_COPY.timelineEye)}
            title={L(ABOUT_COPY.timelineT)}
            subtitle={L(ABOUT_COPY.timelineS)}
            align="center"
          />
          <div className="mt-16 relative">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-[#D4AF37] to-transparent" />
            <div className="space-y-10">
              {timeline.map((tt, i) => (
                <div key={i} className={`relative grid md:grid-cols-2 gap-6 md:gap-14 ${i % 2 ? "md:[&>*:first-child]:order-2" : ""}`}>
                  <div className={`pl-12 md:pl-0 ${i % 2 ? "md:text-left" : "md:text-right"}`}>
                    <div className="overline text-[#B65A14] mb-2">{L(tt.period)}</div>
                    <h3 className="font-serif-display text-2xl text-[#3B2A1A]">{L(tt.title)}</h3>
                  </div>
                  <div className={`pl-12 md:pl-0 ${i % 2 ? "" : "md:pl-12"}`}>
                    <p className="text-[#3B2A1A]/80 leading-relaxed">{L(tt.text)}</p>
                  </div>
                  <div className="absolute left-4 md:left-1/2 -translate-x-1/2 top-1 w-4 h-4 rounded-full bg-[#D4AF37] ring-4 ring-[#F2E7D5]" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <SectionDivider variant="lotus" />

      {/* Core Values */}
      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-8" data-testid="about-values">
        <SectionHeading
          eyebrow={L(ABOUT_COPY.valuesEye)}
          title={L(ABOUT_COPY.valuesT)}
          align="center"
        />
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {CORE_VALUES.map((v) => {
            const Icon = iconMap[v.icon];
            return (
              <div key={v.title} className="relative bg-[#FBF7F0] border border-[#D4AF37]/25 rounded-2xl p-8 card-lift overflow-hidden" data-testid={`about-value-${v.title.toLowerCase()}`}>
                <div className="absolute -top-8 -right-8 opacity-20"><Mandala size={120} color="#D4AF37" opacity={0.8} /></div>
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#B65A14] to-[#3B2A1A] text-[#D4AF37] flex items-center justify-center mb-5">
                  <Icon className="w-6 h-6" />
                </div>
                <div className="overline text-[#B65A14] mb-2">{pick(v, "subtitle")}</div>
                <h3 className="font-serif-display text-2xl text-[#3B2A1A] mb-2">{pick(v, "title")}</h3>
                <p className="text-sm text-[#3B2A1A]/80 leading-relaxed">{pick(v, "desc")}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Statistics */}
      <section className="py-20" data-testid="about-stats">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="bg-[#B65A14] text-[#FBF7F0] rounded-3xl p-10 sm:p-16 relative overflow-hidden">
            <div className="absolute -bottom-20 -right-20 opacity-15 animate-mandala-slow"><Mandala size={340} color="#D4AF37" opacity={1} /></div>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-8 text-center relative">
              {HIGHLIGHTS.map((h) => (
                <div key={h.label}>
                  <div className="font-serif-display text-3xl sm:text-4xl text-[#D4AF37]">{pick(h, "value")}</div>
                  <div className="text-xs uppercase tracking-widest text-[#F2E7D5]/85 mt-2 leading-tight">{pick(h, "label")}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CTASection
        eyebrow={L(ABOUT_COPY.ctaEye)}
        title={L(ABOUT_COPY.ctaT)}
        subtitle={L(ABOUT_COPY.ctaS)}
        primary={{ label: L(ABOUT_COPY.ctaP), to: "/lineage?tab=vadiraja" }}
        secondary={{ label: L(ABOUT_COPY.ctaSbtn), to: "/lineage?tab=guru" }}
        image={IMG.templeInterior}
      />
    </>
  );
};

export const AboutContent = AboutBody;

import PageLayout from "../components/layout/PageLayout";

const About = () => (
  <PageLayout>
    <AboutBody />
  </PageLayout>
);

export default About;
